import * as React from "react";
import {
	Button,
	Card,
	CardContent,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { useParams } from "react-router-dom";

import { CustomFormHelperError } from "../customFormHelper/custom-form-helper-error";
import { FilterFetchSelect } from "../fetch-select";
import { CallBack, FilterOption } from "./types";
import { CustomFormHelperDescription } from "../customFormHelper/custom-form-helper-description";

interface CallBacksProps {
	callBacks: CallBack[];
	onChange: (callBacks: CallBack[]) => void;
	errors: any;
	clearErrors: any;
}

function timeToSeconds(hours: number, minutes: number) {
	return hours * 3600 + minutes * 60;
}

function formatTimeLabel(hours: number, minutes: number): string {
	if (hours === 0 && minutes === 0) return "0 minutes";
	if (hours === 0) return `${minutes} ${minutes === 1 ? "minute" : minutes < 5 ? "minutes" : "minutes"}`;
	if (minutes === 0) return `${hours} ${hours === 1 ? "hour" : hours < 5 ? "hours" : "hours"}`;
	return `${hours} ${hours === 1 ? "hour" : hours < 5 ? "hours" : "hours"} ${minutes} ${minutes === 1 ? "minute" : minutes < 5 ? "minutes" : "minutes"}`;
}

function generateTimeOptions() {
	const options: Array<{ label: string; value: number }> = [];

	for (let minutes = 0; minutes < 60; minutes += 5) {
		if (minutes === 0) continue;
		options.push({
			label: formatTimeLabel(0, minutes),
			value: timeToSeconds(0, minutes),
		});
	}

	for (let hours = 1; hours <= 24; hours++) {
		options.push({
			label: formatTimeLabel(hours, 0),
			value: timeToSeconds(hours, 0),
		});

		if (hours < 24) {
			options.push({
				label: formatTimeLabel(hours, 30),
				value: timeToSeconds(hours, 30),
			});
		}
	}

	return options;
}

export function CallBacks({ callBacks, onChange, errors, clearErrors }: CallBacksProps): React.JSX.Element {
	const { campaignId } = useParams();

	const addCallBack = () => {
		const newCallBack: CallBack = {
			disposition: "",
			time: 3600,
			tries: 3,
		};
		onChange([...callBacks, newCallBack]);
	};

	const removeCallBack = (index: number) => {
		const newCallBacks = callBacks.filter((_, i) => i !== index);
		onChange(newCallBacks);
		clearErrors(`callBacks.${index}.disposition`);
		clearErrors(`callBacks.${index}.time`);
		clearErrors(`callBacks.${index}.tries`);
	};

	const updateCallBack = (index: number, field: keyof CallBack, value: any) => {
		const newCallBacks = [...callBacks];
		newCallBacks[index] = { ...newCallBacks[index], [field]: value };
		onChange(newCallBacks);
		clearErrors(`callBacks.${index}.disposition`);
		clearErrors(`callBacks.${index}.time`);
		clearErrors(`callBacks.${index}.tries`);
	};

	const timeOptions = React.useMemo(() => generateTimeOptions(), []);

	const updateTime = (index: number, totalSeconds: number) => {
		updateCallBack(index, "time", totalSeconds);
	};

	const getCurrentTimeOption = (totalSeconds: number) => {
		const exactMatch = timeOptions.find((opt) => opt.value === totalSeconds);
		if (exactMatch) return exactMatch.value;

		const closest = timeOptions.reduce((prev, curr) => {
			return Math.abs(curr.value - totalSeconds) < Math.abs(prev.value - totalSeconds) ? curr : prev;
		});
		return closest.value;
	};

	return (
		<Stack spacing={2}>
			{callBacks.length === 0 ? (
				<Typography color="text.secondary" variant="body2" sx={{ textAlign: "center", py: 2 }}>
					No call backs added. Click &quot;Add Call Back&quot; to add a call back.
				</Typography>
			) : (
				<Stack spacing={2}>
					{callBacks.map((callBack, index) => {
						return (
							<Card key={index} variant="outlined" sx={{ position: "relative" }}>
								<CardContent sx={{ pb: "16px !important" }}>
									<Grid container spacing={2} alignItems="center">
										<Grid size={{ xs: 12, md: 12 }}>
											<FormControl fullWidth error={!!errors?.callBacks?.[index]?.disposition} required>
												<FilterFetchSelect
													label="Disposition"
													showInputLabel
													placeholder="Select a disposition"
													errorText={errors?.callBacks?.[index]?.disposition?.message}
													onChange={(value: FilterOption | string) => {
														const dispositionValue = typeof value === "string" ? value : (value as FilterOption).value;
														updateCallBack(index, "disposition", dispositionValue);
													}}
													getOptionsUrl={`/campaigns/${campaignId}/dispositions`}
													fetchLabel="name"
													fetchValue="name"
													disabledOptions={callBacks.map((cb) => cb.disposition)}
													value={
														{
															name: "disposition",
															label: callBack.disposition || "",
															value: callBack.disposition || "",
															type: "fetch_select" as any,
														} as FilterOption
													}
                          required
												/>
												{errors?.callBacks?.[index]?.disposition?.message && (
													<CustomFormHelperError text={errors?.callBacks?.[index]?.disposition?.message} />
												)}
											</FormControl>
										</Grid>

										<Grid size={{ xs: 12, md: 6 }}>
											<FormControl fullWidth error={!!errors?.callBacks?.[index]?.tries}>
												<InputLabel>Tries</InputLabel>
												<Select
													value={callBack.tries}
													onChange={(e) => updateCallBack(index, "tries", e.target.value)}
													label="Tries"
												>
													<MenuItem value={1} sx={{ fontSize: "16px !important" }}>
														1
													</MenuItem>
													<MenuItem value={2} sx={{ fontSize: "16px !important" }}>
														2
													</MenuItem>
													<MenuItem value={3} sx={{ fontSize: "16px !important" }}>
														3
													</MenuItem>
												</Select>
                        <CustomFormHelperDescription text='Number of call attempts that will be made for this callback.' />
											</FormControl>
										</Grid>

										<Grid size={{ xs: 12, md: 6 }}>
											<FormControl fullWidth error={!!errors?.callBacks?.[index]?.time}>
												<InputLabel>Time</InputLabel>
												<Select
													value={getCurrentTimeOption(callBack.time)}
													onChange={(e) => updateTime(index, Number(e.target.value))}
													label="Time"
												>
													{timeOptions.map((option) => (
														<MenuItem key={option.value} value={option.value} sx={{ fontSize: "16px !important" }}>
															{option.label}
														</MenuItem>
													))}
												</Select>
                        <CustomFormHelperDescription text='Delay before the next call attempt is made.' />
											</FormControl>
										</Grid>

										<Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
											<Button
												onClick={() => removeCallBack(index)}
												variant="outlined"
												color="error"
												sx={{ alignSelf: "flex-end" }}
												size="small"
											>
												Remove
											</Button>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						);
					})}
				</Stack>
			)}

			<Button
				startIcon={<PlusIcon />}
				onClick={addCallBack}
				variant="outlined"
				size="small"
				sx={{ width: "max-content", alignSelf: "flex-end" }}
			>
				Add Call Back
			</Button>
		</Stack>
	);
}
